import React, { useState } from "react";

import {
  faArrowUpRightFromSquare,
  faClock,
  faEye,
  faRotateRight,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import {
  getOperationStatusPillVariant,
  truncateJson,
} from "src/adminPanel/modulesV2/utils";
import { ModuleOperation, useUpdateOperation } from "src/api/modulesApi";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { Cell, Header } from "src/base-components/Table";
import { ModuleOperationStatus } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { shortDateFromNow } from "src/utils/datetime";
import { formatDuration } from "src/utils/numbers";
import { errorMessage } from "src/utils/stringUtils";

// --- Helpers ----------------------------------------------------------------

const isResolvedStatus = (status: ModuleOperationStatus): boolean =>
  status === ModuleOperationStatus.COMPLETED ||
  status === ModuleOperationStatus.FAILED ||
  status === ModuleOperationStatus.CANCELLED;

// --- Sub-components ---------------------------------------------------------

const DurationBadge: React.FC<{ durationSeconds: number }> = ({
  durationSeconds,
}) => {
  const maxExpected = 600; // 10 min baseline (in seconds)
  const ratio = Math.min(durationSeconds / maxExpected, 1);
  const barColor = (() => {
    if (ratio < 0.25) return "bg-green-400";
    if (ratio < 0.5) return "bg-yellow-400";
    if (ratio < 0.75) return "bg-orange-400";
    return "bg-red-400";
  })();
  const label = formatDuration(durationSeconds * 1000);

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5"
      title={`Duration: ${label}`}
    >
      <Icon icon={faClock} size="2xs" />
      <span className="font-inter-normal-12px text-gray-700">{label}</span>
      <span className="h-1.5 w-10 overflow-hidden rounded-full bg-gray-200">
        <span
          className={twJoin("block h-full rounded-full", barColor)}
          style={{ width: `${Math.max(ratio * 100, 5)}%` }}
        />
      </span>
    </span>
  );
};

const GitHubRunLink: React.FC<{ url: string }> = ({ url }) => (
  <a
    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
    href={url}
    rel="noopener noreferrer"
    target="_blank"
    title="Open GitHub run"
    onClick={(e) => e.stopPropagation()}
  >
    <Icon icon={faArrowUpRightFromSquare} size="2xs" />
    <span className="font-inter-normal-12px">Run</span>
  </a>
);

// --- Actions type -----------------------------------------------------------

export type OperationActions = {
  onView: (operation: ModuleOperation) => void;
  onCancel?: (operation: ModuleOperation) => void;
  onRetry?: (operation: ModuleOperation) => void;
};

// --- Column definitions -----------------------------------------------------

const columnHelper = createColumnHelper<ModuleOperation>();

export const operationColumns = [
  columnHelper.accessor("id", {
    id: "id",
    header: (info) => <Header info={info}>ID</Header>,
    cell: (info) => (
      <Cell info={info}>
        <span className="inline-flex items-center gap-1">
          <span className="font-code-12 text-gray-900">
            {info.getValue().slice(0, 8)}
          </span>
          <span onClick={(e) => e.stopPropagation()}>
            <CopyTextIcon
              feedback="inline"
              size="xs"
              tooltip="Copy operation ID"
              value={info.getValue()}
            />
          </span>
        </span>
      </Cell>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: (info) => <Header info={info}>Status</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Pill variant={getOperationStatusPillVariant(info.getValue())}>
          <Pill.Text fontType="text">{info.getValue()}</Pill.Text>
        </Pill>
      </Cell>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor("createdAt", {
    id: "createdAt",
    header: (info) => <Header info={info}>Created</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Tooltip
          disabled={!info.getValue()}
          title={info.getValue() ?? undefined}
          asChild
        >
          <span className="font-inter-normal-12px text-gray-500">
            {info.getValue() ? shortDateFromNow(info.getValue()!) : "—"}
          </span>
        </Tooltip>
      </Cell>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor("updatedAt", {
    id: "updatedAt",
    header: (info) => <Header info={info}>Updated</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Tooltip
          disabled={!info.getValue()}
          title={info.getValue() ?? undefined}
          asChild
        >
          <span className="font-inter-normal-12px text-gray-500">
            {info.getValue() ? shortDateFromNow(info.getValue()!) : "—"}
          </span>
        </Tooltip>
      </Cell>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor("patch", {
    id: "patch",
    header: (info) => <Header info={info}>Patch</Header>,
    cell: (info) => {
      const actions = info.table.options.meta?.actions as
        | OperationActions
        | undefined;
      return (
        <Cell className="max-w-xs px-4" info={info}>
          <button
            className="block w-full cursor-pointer truncate text-left font-code-12 text-gray-600 hover:text-gray-900"
            type="button"
            onClick={() => actions?.onView(info.row.original)}
          >
            {truncateJson(info.getValue())}
          </button>
        </Cell>
      );
    },
    enableSorting: false,
  }),
  columnHelper.display({
    id: "info",
    header: (info) => <Header info={info}>Info</Header>,
    cell: (info) => {
      const op = info.row.original;
      const duration =
        isResolvedStatus(op.status) && op.tags?.deployment_duration != null
          ? Number(op.tags.deployment_duration)
          : null;
      const githubRunUrl = op.tags?.github_run_url
        ? String(op.tags.github_run_url)
        : null;

      return (
        <Cell info={info}>
          {duration !== null || githubRunUrl ? (
            <span className="inline-flex items-center gap-2">
              {duration !== null && (
                <DurationBadge durationSeconds={duration} />
              )}
              {githubRunUrl && <GitHubRunLink url={githubRunUrl} />}
            </span>
          ) : (
            <span className="font-inter-normal-13px text-gray-400">—</span>
          )}
        </Cell>
      );
    },
  }),
  columnHelper.display({
    id: "actions",
    header: () => <div className="w-10 px-4 py-3" />,
    cell: (info) => {
      const op = info.row.original;
      const actions = info.table.options.meta?.actions as
        | OperationActions
        | undefined;

      const elements: OptionsDropdownElement[] = [
        {
          key: "View patch",
          icon: faEye,
          action: () => actions?.onView(op),
        },
        ...((op.status === ModuleOperationStatus.SCHEDULED ||
          op.status === ModuleOperationStatus.FAILED) &&
        actions?.onCancel
          ? [
              {
                key: "Cancel",
                icon: faXmark,
                action: () => actions.onCancel!(op),
              },
            ]
          : []),
        ...(op.status === ModuleOperationStatus.FAILED && actions?.onRetry
          ? [
              {
                key: "Retry",
                icon: faRotateRight,
                action: () => actions.onRetry!(op),
              },
            ]
          : []),
      ];

      return (
        <Cell info={info}>
          <EllipsisOptionsDropdown
            dataLoc="operation-actions"
            elements={elements}
            iconSize="xs"
          />
        </Cell>
      );
    },
  }),
];

// --- Shared confirm hook ----------------------------------------------------

type ConfirmAction = {
  operation: ModuleOperation;
  type: "cancel" | "retry";
};

export const useOperationConfirm = () => {
  const { mutateAsync: updateOperation } = useUpdateOperation();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const openConfirm = (action: ConfirmAction) => {
    setConfirmAction(action);
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { operation, type } = confirmAction;
    const targetStatus =
      type === "cancel"
        ? ModuleOperationStatus.CANCELLED
        : ModuleOperationStatus.SCHEDULED;
    const actionLabel = type === "cancel" ? "cancel" : "retry";

    try {
      await updateOperation({
        operationId: operation.id,
        request: {
          data: {
            type: "module_operations",
            id: operation.id,
            attributes: { status: targetStatus },
          },
        },
      });
    } catch (err) {
      toastActions.failure({
        title: `Failed to ${actionLabel} operation`,
        description: errorMessage(err) || "An unexpected error occurred",
      });
    }
    setIsConfirmOpen(false);
  };

  return {
    confirmAction,
    isConfirmOpen,
    openConfirm,
    handleConfirm,
    clearConfirm: () => setConfirmAction(null),
    closeConfirm: () => setIsConfirmOpen(false),
  };
};
